import { useTranslation } from "react-i18next";
import useAuth from "../../contexts/AuthContext";
import DashboardLayout, { DashboardPageLayout } from "../../layout/dashboard";
import OpenPageLayout from "../../layout/openPages/OpenPageLayout";
import ContentWrapper from "../../styles/ContentWrapper";

function PrivacyPolicy() {
	return (<>
			<h1>SmartOcean Privacy Policy</h1>

			<h2>1. Introduction</h2>
			<p>
				Smart Ocean AS (operating as SmartOcean, registered in Norway, headquartered in Grimstad) is a marine
				technology company that develops and operates a digital platform connected to buoys. These buoys
				primarily provide real-time GPS position data to monitor their geographical location, while wave height
				measurements are offered as an additional service.
			</p>
			<p>
				This Privacy Policy provides further information on which personal data SmartOcean ("we", "us", and/or
				"our") process, how we process it, for what purposes and our legal basis. The Privacy Policy also
				explains what your rights you have as a data subject and how you can exercise them.
			</p>

			<h2>2. Why and how we process personal data</h2>

			<h3>2.1 Overview of our processing of personal data</h3>
			<p>
				We process your personal data to enable access to and use of our services by providing real-time GPS
				positioning of your buoy and allowing you to monitor wave height data as an added service. Your
				information is also used to administer your user account and facilitate communications, such as
				notifications or updates about our services.
			</p>
			<p>
				In addition, we analyse aggregated and de-identified data to improve our offerings. We may also share
				aggregated buoy data—which excludes directly identifying personal information—with third parties, such
				as weather services.
			</p>
			<p>
				Further information about our purposes for processing personal data is set out below.
			</p>

			<h3>2.2 Process orders and receiving payments</h3>
			<p>
				In order for us to process orders and receive payment we have to process your personal data. For this
				purpose we may need to process:
			</p>
			<ul>
				<li>Name</li>
				<li>E-mail address</li>
				<li>Postal/billing address</li>
				<li>Credit card information</li>
			</ul>
			<p>
				The legal basis is our legitimate interest in providing and receiving payment, see Article 6(1)(f) of
				the GDPR. We retain the data for 5 years in accordance with our obligations under the bookkeeping
				legislation.
			</p>

			<h3>2.3 Analysis and development</h3>
			<p>
				To evaluate, improve and optimize our products and services, we may analyze your use of our services
				and/or website. We also analyse aggregated and de-identified data to improve our offerings.
			</p>
			<p>
				The legal basis for this processing is our legitimate interest in improving our services and products,
				see Article 6(1)(f) of the GDPR. We will retain your personal data as long as necessary in order to
				achieve the purpose for which they were collected, which period will generally not exceed three years.
			</p>

			<h3>2.4 Customer service and feedback</h3>
			<p>
				To provide customer service, we will process your contact information (email address or telephone
				number) as well as the content in your message/inquiry to us. Depending on the assistance requested, we
				may ask you to submit additional personal data in order to provide you proper and appropriate customer
				service.
			</p>
			<p>
				The legal basis is our legitimate interest in providing a pleasant and adequate customer experience, see
				Article 6(1)(f) of the GDPR. We will retain your personal data as long as necessary in order to achieve
				the purposes for which they were collected, this period will generally not exceed three years.
			</p>

			<h3>2.5 Marketing and communication</h3>
			<p>
				We may send you, or otherwise notify you about, various offers and activities, e.g., in the form of
				newsletters or special offers. For this purpose, we will process your name, e-mail address and other
				personal data related to the special offer.
			</p>
			<p>
				The legal basis for processing personal data for direct marketing is consent, unless you’re an existing
				customer (in which case we base our direct marketing on our legitimate interest). For other forms of
				marketing, the legal basis is our legitimate interest in marketing our products and services, see
				Article 6(1)(f) of the GDPR.
			</p>
			<p>
				We will retain your personal data as long as necessary in order to achieve the purposes for which they
				were collected, this period will generally not exceed three years.
			</p>

			<h2>3. Third-Party Providers and Data Transfers</h2>
			<p>
				We may transfer your personal data to our IT service providers that are established outside of the
				EU/EEA. When third party service providers process your personal data outside of the EU/EEA, such
				transfer will either be subject to:
			</p>
			<ul>
				<li>An EU Commission adequacy decision</li>
				<li>An EU approved certification mechanism</li>
				<li>The European Commission’s standard contractual clauses</li>
			</ul>
			<p>
				We rely on reputable third-party providers to ensure the security and functionality of our platform,
				including:
			</p>
			<ul>
				<li><strong>Microsoft Azure</strong> – secure data storage with servers located within the EU</li>
				<li><strong>Digital Ocean</strong> – secure data storage with servers located within the EU</li>
				<li><strong>Google Firebase</strong> – authentication and two-factor verification</li>
				<li><strong>Stripe</strong> – external payment processor handling transactions securely</li>
			</ul>

			<h2>4. Your rights</h2>
			<p>
				You have a number of rights in relation to processing of your personal data. If you have any questions
				or want to enforce your rights, please contact us using the information at the bottom of this notice.
			</p>
			<p>
				Your rights include:
			</p>
			<ul>
				<li><strong>Right to information and access</strong></li>
				<li><strong>Right to object</strong></li>
				<li><strong>Right to erasure</strong></li>
				<li><strong>Right to rectification</strong></li>
				<li><strong>Right to restriction of processing</strong></li>
				<li><strong>Right to withdraw consent</strong></li>
				<li><strong>Right to complaint</strong> – you may contact the Norwegian Data Protection Authority</li>
			</ul>

			<h2>5. Cookies</h2>
			<p>
				Our platform uses cookies and similar tracking technologies to enhance your user experience, ensure
				proper functionality, and collect technical data.
			</p>
			<p>
				Please read more about our use of cookies in our cookie policy.
			</p>

			<h2>6. Contact information</h2>
			<p>
				For any questions or to exercise your rights, please contact us:
			</p>
			<ul>
				<li><strong>Email:</strong> support@smartocean.com</li>
				<li><strong>Website:</strong> www.smartocean.com</li>
				<li><strong>Address:</strong> Smart Ocean AS, Bark Silas vei 5, 4876 Grimstad</li>
			</ul>

			<hr/>

			<h1>Terms of Use for SmartOcean (Business)</h1>

			<h2>Introduction</h2>
			<p>
				Welcome to SmartOcean’s digital platform, which includes our website, mobile application, and all
				related services (“Services”). These terms of use (“Terms”) constitute a legally binding agreement
				between Smart Ocean AS (“SmartOcean”) and the entity agreeing to these Terms (“Customer”).
			</p>
			<p>
				These Terms take effect when the Customer registers or reactivates an account or starts using the
				Services.
			</p>

			<h2>User Registration and Corporate Account Responsibility</h2>
			<p>
				To access the Services, the Customer must create a corporate account. The Customer is responsible for
				maintaining the confidentiality of account credentials and all activities conducted under the account.
			</p>

			<h2>Payment and Subscription</h2>
			<p>
				Access to the Services requires an active subscription. Subscriptions are billed on a recurring basis
				unless otherwise specified, and all fees are non-refundable. Prices may change upon prior written
				notice.
			</p>

			<h2>Restrictions on use and suspension</h2>
			<p>
				The Customer must not:
			</p>
			<ul>
				<li>Sell, lease, rent, or provide access to the Services to third parties</li>
				<li>Share credentials with unauthorized users</li>
				<li>Violate applicable laws</li>
				<li>Upload illegal, harmful, or infringing content</li>
				<li>Circumvent security measures</li>
				<li>Reverse engineer the Services</li>
				<li>Use the Services to develop competing products</li>
			</ul>
			<p>
				Misuse may result in suspension or termination.
			</p>

			<h2>Service Functionality and Data Collection</h2>
			<p>
				The Services display real-time GPS position of the Customer’s buoy, with optional wave height data. Data
				is continuously collected and displayed in the Customer’s dashboard.
			</p>
			<p>
				Ownership of collected data vests in SmartOcean to the maximum extent permitted by law.
			</p>

			<h2>Personal data</h2>
			<p>
				Processing of personal data is governed by the SmartOcean Data Processing Terms.
			</p>

			<h2>Ownership and Intellectual Property</h2>
			<p>
				All Intellectual Property Rights related to the Services are the exclusive property of SmartOcean or its
				partners.
			</p>

			<h2>Limitation of Liability and Disclaimers</h2>
			<p>
				The Services are provided “as is” and “as available”. Liability is limited to direct damages up to the
				fees paid or NOK 50,000, whichever is less.
			</p>

			<h2>Modifications</h2>
			<p>
				SmartOcean may modify the Services or Terms at any time. Continued use constitutes acceptance.
			</p>

			<h2>Governing Law and Jurisdiction</h2>
			<p>
				These Terms are governed by Norwegian law. Disputes shall be resolved by the courts of Oslo, Norway.
			</p>

			<h2>Contact Information</h2>
			<ul>
				<li><strong>Email:</strong> support@smartocean.com</li>
				<li><strong>Website:</strong> www.smartocean.com</li>
			</ul>

			<hr/>

			<h1>Terms of Use for SmartOcean (B2C)</h1>

			<h2>Introduction</h2>
			<p>
				These Terms constitute a legally binding agreement between SmartOcean and the private person
				(“Customer”) using the Services.
			</p>

			<h2>User Registration and Account Responsibility</h2>
			<p>
				The Customer is responsible for safeguarding account credentials and all activities conducted under the
				account.
			</p>

			<h2>Payment and Subscription</h2>
			<p>
				Subscriptions are billed on a recurring basis and are non-refundable. Price increases exceeding CPI
				allow termination upon written notice.
			</p>

			<h2>Termination</h2>
			<p>
				The Customer may terminate the subscription effective at the end of the current subscription period.
			</p>

			<h2>Restrictions on use and suspension</h2>
			<p>
				The same restrictions as set out in the Business Terms apply.
			</p>

			<h2>Service Functionality and Data Collection</h2>
			<p>
				The Services display real-time GPS position and optional wave height data.
			</p>

			<h2>Personal data</h2>
			<p>
				Processing of personal data is governed by the SmartOcean Data Processing Terms.
			</p>

			<h2>Ownership and Intellectual Property</h2>
			<p>
				All Intellectual Property Rights related to the Services are owned by SmartOcean.
			</p>

			<h2>Limitation of Liability and Disclaimers</h2>
			<p>
				The Services are provided “as is” without warranties. SmartOcean is not liable for personal injury or
				losses related to the consumer's business.
			</p>

			<h2>Modifications</h2>
			<p>
				Material modifications entitle the Customer to terminate the Agreement.
			</p>

			<h2>Governing Law and Jurisdiction</h2>
			<p>
				Norwegian law applies. Courts of Oslo have exclusive jurisdiction.
			</p>

			<h2>Contact Information</h2>
			<ul>
				<li><strong>Email:</strong> support@smartocean.com</li>
				<li><strong>Website:</strong> www.smartocean.com</li>
			</ul>

			<hr/>

			<h2>Special provisions for Norwegian consumers</h2>
			<p>
				This section applies only to Norwegian consumers.
			</p>
			<p>
				Denne Avtalen gjelder digitalt innhold som ikke leveres på et fysisk medium. Leveringen av Tjenestene
				begynner umiddelbart ved avtaleinngåelse. Ved å inngå denne Avtalen samtykker du til at angreretten går
				tapt i henhold til angrerettloven § 22 bokstav n.
			</p>
			<p>
				For informasjon om forbrukeres lovbestemte rettigheter vises det til digitalytelsesloven.
			</p>
			<p>
				Forbrukertilsynet kan mekle i tvister.<br/>
				Se mer informasjon: <a
				href="https://www.forbrukertilsynet.no/send-sak-til-mekling">https://www.forbrukertilsynet.no/send-sak-til-mekling</a>
			</p>
		</>
	);
}

export function PrivacyPolicyPage() {
	const { t } = useTranslation();
	const { isAuthenticated } = useAuth();

	if (isAuthenticated) {
		return (
			<DashboardLayout>
				<DashboardPageLayout pageName={t("menu.general.privacyPolicy")}>
					<PrivacyPolicy />
				</DashboardPageLayout>
			</DashboardLayout>
		);
	}

	return (
		<OpenPageLayout>
			<ContentWrapper>
				<PrivacyPolicy />
			</ContentWrapper>
		</OpenPageLayout>
	);
}

export default PrivacyPolicy;
